import { createElement, type ComponentType } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  EntityGrafanaAlertsCard,
  type AlertsCardOpts,
  isAlertSelectorAvailable,
  isDashboardSelectorAvailable,
} from '@backstage-community/plugin-grafana';

// Replacement for the stock `entity-card:grafana/alerts` card. The plugin's
// /alpha extension hardcodes `jsx(AlertsCard, {})` — it passes no opts and
// exposes no config-schema input, so `showState` (the "State" column with
// colored StatusOK/Pending/Warning/Error badges) cannot be turned on via
// app-config. The package ROOT export `EntityGrafanaAlertsCard(opts)` does
// accept AlertsCardOpts, so we render it here with `showState`.
//
// The stock card is disabled in app-config.yaml (`- entity-card:grafana/alerts:
// false`) so the entity page renders only this one. The filter mirrors the
// stock card exactly so it attaches to the same entities (those carrying the
// grafana dashboard- or alert-label-selector annotation).
//
// EntityGrafanaAlertsCard is imported eagerly (top-level) rather than via
// `await import` inside the loader, matching the goalert module: a dynamic
// import creates a separate webpack chunk whose hash changes on deploy, and a
// browser holding a cached main bundle then 404s the old chunk URL and React
// silently drops the card. Eager import keeps the card in the main bundle so
// cache-invalidation lines up.
const grafanaAlertsCard = EntityCardBlueprint.make({
  name: 'grafana-alerts-with-state',
  params: {
    filter: (entity: Parameters<typeof isAlertSelectorAvailable>[0]) =>
      Boolean(isDashboardSelectorAvailable(entity)) ||
      isAlertSelectorAvailable(entity),
    // Render via createElement (deferred), NOT by calling
    // EntityGrafanaAlertsCard({...}) directly. The export is the
    // plugin.provide(createComponentExtension(...)) wrapper, which runs React
    // hooks when invoked; calling it inside this async loader (outside React's
    // render) is a hooks-rules violation that tsc won't catch but the browser
    // will. createElement defers invocation to render. It's typed
    // `(opts?: AlertsCardOpts) => JSX.Element`, not a React.FC, so cast to a
    // ComponentType<AlertsCardOpts> to pass `showState`/`title` as props (React
    // calls it with these as `opts` at render — same contract).
    loader: async () =>
      createElement(
        EntityGrafanaAlertsCard as unknown as ComponentType<AlertsCardOpts>,
        { showState: true, title: 'Alerts' },
      ),
  },
});

export const grafanaAlertsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [grafanaAlertsCard],
});
